<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto">
      <UserTable
        :create-enabled="true"
        :editable="true"
        :loading="isLoading"
        :removable="true"
        :rows="users"
        :search-enabled="true"
        :search-query="searchQuery"
        :has-more-items="hasNextPage"
        @create="createModalVisible = true"
        @edit="editRow"
        @click-row="(user: User) => redirectToDetails(router, user.id, 'users.overview')"
        @remove="(user: User) => confirmRemove(user)"
        @search="(query: string) => search(query)"
        @load="!isFetching && fetchNextPage()"
      />

      <CreateUserModal
        v-if="createModalVisible"
        :loading="isCreating"
        @create="(user: CreateUser) => create(user)"
        @close="createModalVisible = false"
      />

      <UpdateUserPasswordModal
        v-if="updateModalVisible && selectedRow"
        :user="selectedRow"
        :loading="isUpdating"
        @update="(user: User, newPassword: string) => update(user, newPassword)"
        @close="updateModalVisible = false"
      />

      <InfuseConfirmModal
        v-if="removeModalVisible"
        :action-title="t('e_connector.users.modal.remove.title')"
        verification-text="delete"
        @confirm="remove"
        @close="removeModalVisible = false"
      />
    </div>
  </InfusePageLayout>
</template>

<script setup lang="ts">
import { InfusePageLayout } from '@infuse/ui/layouts';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { replaceQueryParams } from '@infuse/ui/utils';
import { redirectToDetails } from '@infuse/e-connector/shared/utils';
import { CreateUserModal, UpdateUserPasswordModal, UserTable } from '@infuse/e-connector/user/ui';
import { User } from '@infuse/e-connector/user/domain';
import { useUsers } from '@infuse/e-connector/user/queries';
import { CreateUser } from '@infuse/api/e-connector/user';
import { useCreateUser, useDeleteUser, useUpdateUserPassword } from '@infuse/e-connector/user/mutations';
import { InfuseConfirmModal } from '@infuse/ui/components';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { users, searchQuery, isLoading, hasNextPage, isFetching, fetchNextPage } = useUsers();
const { mutateAsync: createUser, isPending: isCreating } = useCreateUser();
const { mutateAsync: updateUserPassword, isPending: isUpdating } = useUpdateUserPassword();
const { mutateAsync: deleteUser } = useDeleteUser();

const createModalVisible = ref<boolean>(false);
const updateModalVisible = ref<boolean>(false);
const removeModalVisible = ref<boolean>(false);
const selectedRow = ref<User>();

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

function search(search: string) {
  searchQuery.value = search;
  replaceQueryParams(router, 'users', { search: searchQuery.value });
}

async function create(user: CreateUser) {
  await createUser(user);
  createModalVisible.value = false;
}

async function update(user: User, newPassword: string) {
  await updateUserPassword({ user, newPassword });
  updateModalVisible.value = false;
}

function editRow(row: User) {
  selectedRow.value = row;
  updateModalVisible.value = true;
}

function confirmRemove(row: User) {
  selectedRow.value = row;
  removeModalVisible.value = true;
}

async function remove() {
  if (!selectedRow.value) {
    return;
  }

  await deleteUser(selectedRow.value.id);

  removeModalVisible.value = false;
  selectedRow.value = undefined;
}
</script>
